import React, { useState, useEffect, useCallback, useMemo } from "react";
import { fetchData } from "../utils/api.js";
import PropTypes from "prop-types";
import "./TeamDashboard.css";

import { useParams, useNavigate } from "react-router";
import { setHeaderTitle } from "./Header.js";
import TeamDetailView from "./TeamDetailView.js";
import TeamDashboardHome from "./TeamDashboardHome.js";

const TeamDashboard = ({ user }) => {
  const { teamId } = useParams();
  const navigate = useNavigate();

  const [viewingTeam, setViewingTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [publicTeams, setPublicTeams] = useState([]);
  const [selectedTeamOverrideId, setSelectedTeamOverrideId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [leagues, setLeagues] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const fetchTeams = useCallback(async () => {
    if (!user?.firebaseUid) return;
    try {
      const data = await fetchData(
        `/user_teams?firebase_id=eq.${user.firebaseUid}`,
      );
      setTeams(data || []);
    } catch (error) {
      console.error("Failed to fetch teams:", error);
    }
  }, [user]);

  const fetchLeagues = useCallback(async () => {
    try {
      const data = await fetchData("/leagues");
      setLeagues(data || []);
    } catch (error) {
      console.error("Failed to fetch leagues:", error);
    }
  }, []);

  useEffect(() => {
    const loadTeamFromUrl = async () => {
      if (!teamId) {
        setViewingTeam(null);
        return;
      }
      try {
        const data = await fetchData(`/teams?team_id=eq.${teamId}`);
        if (data && data.length > 0) {
          setViewingTeam(data[0]);
        } else {
          setViewingTeam({ team_id: teamId, notFoundOrLoading: true });
        }
      } catch (error) {
        console.error("Failed to fetch specific team:", error);
        setViewingTeam({ team_id: teamId, notFoundOrLoading: true });
      }
    };

    void loadTeamFromUrl();
  }, [teamId, teams, user]);

  useEffect(() => {
    if (viewingTeam && !viewingTeam.notFoundOrLoading) {
      setHeaderTitle(viewingTeam.team_name);
    } else if (showCreate) {
      setHeaderTitle("Create Team");
    } else {
      setHeaderTitle("");
    }
    return () => setHeaderTitle("");
  }, [viewingTeam, showCreate]);

  useEffect(() => {
    void fetchLeagues();

    if (user?.firebaseUid) {
      setIsLoading(true);
      fetchTeams().finally(() => setIsLoading(false));
    } else {
      setTeams([]);
      setIsLoading(true);
      fetchData("/teams")
        .then((pub) => {
          setPublicTeams(Array.isArray(pub) ? pub : []);
        })
        .catch(() => {
          setPublicTeams([]);
        })
        .finally(() => setIsLoading(false));
    }
  }, [user, fetchLeagues, fetchTeams]);

  const availableTeams = useMemo(
    () => (teams.length > 0 ? teams : publicTeams),
    [teams, publicTeams],
  );

  const handleTeamCreated = (newTeam) => {
    void fetchTeams();
    setShowCreate(false);
    if (newTeam?.team_id) navigate(`/team/${newTeam.team_id}`);
  };

  if (isLoading && availableTeams.length === 0 && !teamId) {
    return <div className="loading-spinner" />;
  }

  const resolveCurrentTeam = () => {
    if (viewingTeam && !viewingTeam.notFoundOrLoading) {
      return viewingTeam;
    }
    if (selectedTeamOverrideId) {
      const override = availableTeams.find(
        (t) => String(t.team_id || t.id) === String(selectedTeamOverrideId),
      );
      if (override) return override;
    }
    return availableTeams[0] || null;
  };

  const currentTeam = resolveCurrentTeam();

  const handleSelectTeam = (nextTeamId) => {
    if (teamId) {
      navigate(`/team/${nextTeamId}`);
    } else {
      setSelectedTeamOverrideId(nextTeamId);
    }
  };

  return (
    <div className="team-dashboard">
      <TeamDashboardHome
        teams={availableTeams}
        leagues={leagues}
        user={user}
        showCreate={showCreate}
        setShowCreate={setShowCreate}
        onTeamCreated={handleTeamCreated}
        hasActiveTeam={Boolean(currentTeam)}
      />
      {currentTeam && (
        <TeamDetailView
          team={currentTeam}
          teams={availableTeams}
          user={user}
          onSelectTeam={handleSelectTeam}
          onOpenCreateTeam={() => setShowCreate(true)}
        />
      )}
    </div>
  );
};

TeamDashboard.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    firebaseUid: PropTypes.string,
    getIdToken: PropTypes.func,
  }),
};

export default TeamDashboard;
